import React, { useState, useEffect } from 'react';
import './AdminContent.css';

const AdminContent = ({ selectedSection, certNumber }) => {
    const [listings, setListings] = useState([]);
    const [certs, setCerts] = useState([]);
    const [cards, setCards] = useState([]);
    const [orders, setOrders] = useState([]);
    const [sets, setSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchCertNumber, setSearchCertNumber] = useState(certNumber || '');
    const [selectedCert, setSelectedCert] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mappingData, setMappingData] = useState({
        promo: {
            promo_id: ''
        },
        pricecharting: {
            price_charting_id: ''
        },
        gamestop: {
            gamestop_id: ''
        }
    });
    const [newPromo, setNewPromo] = useState({
        display_name: '',
        release_date: ''
    });
    const [isCreatePromoModalOpen, setIsCreatePromoModalOpen] = useState(false);
    const [promos, setPromos] = useState([]);
    const [modalPromos, setModalPromos] = useState([]);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        setLoading(true);
        setError(null);

        if (selectedSection === 'Listings') {
            fetchListings();
        } else if (selectedSection === 'Certs') {
            fetchCerts();
        } else if (selectedSection === 'Cards') {
            fetchCards();
        } else if (selectedSection === 'Orders') {
            fetchOrders();
        } else if (selectedSection === 'Sets') {
            fetchSets();
        } else if (selectedSection === 'Promo') {
            fetchPromos();
        } else {
            setLoading(false);
        }
    }, [selectedSection, page]);

    const fetchListings = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/listings?page=${page}`);
            if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
            const result = await response.json();
            setListings(result.listings);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCerts = async () => {
        try {
            const params = new URLSearchParams();
            params.append('hasImages', 'true');
            params.append('page', page);
            params.append('count', '50');
            if (searchCertNumber) {
                params.append('certNumber', searchCertNumber);
            }
            const response = await fetch(`${apiBaseUrl}/certs?${params.toString()}`);
            if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
            const result = await response.json();
            setCerts(result.certs || []);
            setTotalPages(Math.ceil((result.total || 0) / 50));
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCards = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/cards?page=${page}`);
            if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
            const result = await response.json();
            setCards(result.cards);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrders = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/orders?page=${page}`);
            if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
            const result = await response.json();
            setOrders(result.orders);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchSets = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/sets?page=${page}`);
            if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
            const result = await response.json();
            setSets(result);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchPromos = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/promos`);
            if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
            const result = await response.json();
            // Sort promos by release date in reverse chronological order
            const sortedPromos = result.promos.sort((a, b) => {
                // Handle cases where release_date might be null
                if (!a.release_date) return 1;
                if (!b.release_date) return -1;
                return new Date(b.release_date) - new Date(a.release_date);
            });
            setPromos(sortedPromos);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCertNumberClick = (certNumber) => {
        setSearchCertNumber(certNumber);
        fetchCerts();
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleMapClick = async (cert) => {
        setSelectedCert(cert);
        setMappingData({
            promo: {
                promo_id: cert.promo?.promo_id || ''
            },
            pricecharting: {
                price_charting_id: cert.price_charting_id || ''
            },
            gamestop: {
                gamestop_id: cert.gamestop_id || ''
            }
        });
        setIsModalOpen(true);

        // Fetch promos for the dropdown
        try {
            const response = await fetch(`${apiBaseUrl}/promos`);
            if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
            const result = await response.json();
            setModalPromos(result.promos);
        } catch (error) {
            setError(error.message);
        }
    };

    const handleMappingChange = (section, field, value) => {
        setMappingData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleSaveMapping = async (section) => {
        try {
            let response;
            if (section === 'promo') {
                const selectedPromo = modalPromos.find(p => p.promo_id === mappingData.promo.promo_id);
                response = await fetch(`${apiBaseUrl}/promos/map`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        promo_id: selectedPromo.id,
                        cert_number: selectedCert.cert_number
                    })
                });
            } else if (section === 'pricecharting') {
                response = await fetch(`${apiBaseUrl}/cert/pricecharting/map`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        cert_number: selectedCert.cert_number,
                        price_charting_id: mappingData.pricecharting.price_charting_id
                    })
                });
            } else {
                response = await fetch(`${apiBaseUrl}/certs/${selectedCert.cert_number}/mapping`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        [section]: mappingData[section]
                    })
                });
            }
            
            if (!response.ok) throw new Error('Failed to save mapping');
            
            // Refresh the certs list
            fetchCerts();
            setIsModalOpen(false);
        } catch (error) {
            setError(error.message);
        }
    };

    const handleCreatePromo = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/promos`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(newPromo)
            });
            
            if (!response.ok) throw new Error('Failed to create promo');
            
            // Clear the form and close modal
            setNewPromo({
                display_name: '',
                release_date: ''
            });
            setIsCreatePromoModalOpen(false);
            
            // Refresh the promos list
            fetchPromos();
        } catch (error) {
            setError(error.message);
        }
    };

    if (loading) return <p></p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <div className="admin-content">
            <h2>{selectedSection}</h2>

            {selectedSection === 'Promo' && (
                <div className="admin-section">
                    <div className="section-header" style={{ marginBottom: '20px' }}>
                        <button 
                            className="section-save-button"
                            onClick={() => setIsCreatePromoModalOpen(true)}
                        >
                            Create New Promo
                        </button>
                    </div>
                    <div className="section-content">
                        {!loading && !error && promos.length > 0 && (
                            <table className="listings-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Promo ID</th>
                                        <th>Display Name</th>
                                        <th>Release Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {promos.map((promo) => (
                                        <tr key={promo.promo_id}>
                                            <td>{promo.id}</td>
                                            <td>{promo.promo_id}</td>
                                            <td>{promo.display_name || '-'}</td>
                                            <td>{promo.release_date ? new Date(promo.release_date).toLocaleDateString() : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {error && <p className="error-message">Error loading promos: {error}</p>}
                    </div>
                </div>
            )}

            {/* Create Promo Modal */}
            {isCreatePromoModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Create New Promo</h3>
                        <div className="mapping-section">
                            <div className="mapping-field">
                                <label>Display Name:</label>
                                <input
                                    type="text"
                                    value={newPromo.display_name}
                                    onChange={(e) => setNewPromo(prev => ({
                                        ...prev,
                                        display_name: e.target.value
                                    }))}
                                    placeholder="Enter display name"
                                />
                            </div>
                            <div className="mapping-field">
                                <label>Release Date:</label>
                                <input
                                    type="date"
                                    value={newPromo.release_date}
                                    onChange={(e) => setNewPromo(prev => ({
                                        ...prev,
                                        release_date: e.target.value
                                    }))}
                                />
                            </div>
                            <div className="modal-buttons">
                                <button 
                                    className="section-save-button"
                                    onClick={handleCreatePromo}
                                    disabled={!newPromo.display_name || !newPromo.release_date}
                                >
                                    Create
                                </button>
                                <button onClick={() => setIsCreatePromoModalOpen(false)}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedSection === 'Certs' && (
                <div>
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="Search by Cert Number..."
                            value={searchCertNumber}
                            onChange={(e) => setSearchCertNumber(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && searchCertNumber.trim() !== '') {
                                    setPage(1);
                                    fetchCerts();
                                }
                            }}
                        />
                        <button onClick={() => {
                            setPage(1);
                            fetchCerts();
                        }}>Search</button>
                    </div>
                    {certs.length > 0 && (
                        <>
                            <table className="certs-table">
                                <thead>
                                    <tr>
                                        <th>Map</th>
                                        <th>Cert Number</th>
                                        <th>Name</th>
                                        <th>Grade</th>
                                        <th>Set</th>
                                        <th>Set Number</th>
                                        <th>Year</th>
                                        <th>Total Population</th>
                                        <th>Promo ID</th>
                                        <th>Images</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {certs.map((cert) => (
                                        <tr key={cert.id}>
                                            <td>
                                                <button 
                                                    className="map-button"
                                                    onClick={() => handleMapClick(cert)}
                                                >
                                                    Map
                                                </button>
                                            </td>
                                            <td>{cert.cert_number}</td>
                                            <td>{cert.name}</td>
                                            <td>{cert.grade}</td>
                                            <td>{cert.set}</td>
                                            <td>{cert.set_number}</td>
                                            <td>{cert.year}</td>
                                            <td>{cert.total_population}</td>
                                            <td>{cert.promo?.promo_id || '-'}</td>
                                            <td>
                            <div className="cert-images">
                                                    {cert.cert_images?.front_image_url && (
                                <img 
                                    src={cert.cert_images.front_image_url} 
                                    alt="Front" 
                                    className="cert-image" 
                                    onClick={() => window.open(`https://gradies.com/listing/${cert.cert_number}`, '_blank')}
                                />
                                                    )}
                                                    {cert.cert_images?.back_image_url && (
                                <img 
                                    src={cert.cert_images.back_image_url} 
                                    alt="Back" 
                                    className="cert-image" 
                                    onClick={() => window.open(`https://gradies.com/listing/${cert.cert_number}`, '_blank')}
                                />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="pagination">
                                <button 
                                    onClick={() => handlePageChange(page - 1)} 
                                    disabled={page === 1}
                                >
                                    Previous
                                </button>
                                <span>Page {page} of {totalPages}</span>
                                <button 
                                    onClick={() => handlePageChange(page + 1)} 
                                    disabled={page === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Mapping Modal */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Map Cert #{selectedCert?.cert_number}</h3>
                        
                        <div className="mapping-sections-container">
                            {/* Promo Section */}
                            <div className="mapping-section">
                                <h4>Promo</h4>
                                <div className="mapping-field">
                                    <label>Select Promo:</label>
                                    <select
                                        value={mappingData.promo.promo_id}
                                        onChange={(e) => handleMappingChange('promo', 'promo_id', e.target.value)}
                                    >
                                        <option value="">Select a promo...</option>
                                        {modalPromos.map((promo) => (
                                            <option key={promo.promo_id} value={promo.promo_id}>
                                                {promo.display_name || promo.promo_id}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button 
                                    className="section-save-button"
                                    onClick={() => handleSaveMapping('promo')}
                                >
                                    Save Promo
                                </button>
                            </div>

                            {/* PriceCharting Section */}
                            <div className="mapping-section">
                                <h4>PriceCharting</h4>
                                <div className="mapping-field">
                                    <label>PriceCharting ID:</label>
                                    <input
                                        type="text"
                                        value={mappingData.pricecharting.price_charting_id}
                                        onChange={(e) => handleMappingChange('pricecharting', 'price_charting_id', e.target.value)}
                                    />
                                </div>
                                <button 
                                    className="section-save-button"
                                    onClick={() => handleSaveMapping('pricecharting')}
                                >
                                    Save PriceCharting
                                </button>
                            </div>

                            {/* GameStop Section */}
                            <div className="mapping-section">
                                <h4>GameStop</h4>
                                <div className="mapping-field">
                                    <label>GameStop ID:</label>
                                    <input
                                        type="text"
                                        value={mappingData.gamestop.gamestop_id}
                                        onChange={(e) => handleMappingChange('gamestop', 'gamestop_id', e.target.value)}
                                    />
                                </div>
                                <button 
                                    className="section-save-button"
                                    onClick={() => handleSaveMapping('gamestop')}
                                >
                                    Save GameStop
                                </button>
                            </div>
                        </div>

                        <div className="modal-buttons">
                            <button onClick={() => setIsModalOpen(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {selectedSection === 'Listings' && listings.length > 0 && (
                <table className="listings-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Listing ID</th>
                            <th>Seller Email</th>
                            <th>Cert Number</th>
                            <th>Card Name</th>
                            <th>Price</th>
                            <th>Wholesale Price</th>
                            <th>Is Active</th>
                            <th>Card ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {listings.map((listing) => (
                            <tr key={listing.id}>
                                <td>{listing.id}</td>
                                <td>{listing.listing_id}</td>
                                <td>{listing.seller_email}</td>
                                <td>
                                    <a href="#" onClick={() => handleCertNumberClick(listing.cert_number)}>
                                        {listing.cert_number}
                                    </a>
                                </td>
                                <td>{listing.card_name}</td>
                                <td>{listing.price}</td>
                                <td>{listing.wholesale_price}</td>
                                <td>{listing.is_active ? 'Yes' : 'No'}</td>
                                <td>{listing.card_id}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {selectedSection === 'Cards' && cards.length > 0 && (
                <table className="cards-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Supertype</th>
                            <th>HP</th>
                            <th>Rarity</th>
                            <th>Artist</th>
                            <th>Set Name</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cards.map((card) => (
                            <tr key={card.id}>
                                <td>{card.id}</td>
                                <td>{card.name}</td>
                                <td>{card.supertype}</td>
                                <td>{card.hp}</td>
                                <td>{card.rarity}</td>
                                <td>{card.artist}</td>
                                <td>{card.set.name}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {selectedSection === 'Orders' && orders.length > 0 && (
                <table className="orders-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Buyer Email</th>
                            <th>Seller Email</th>
                            <th>Cert Number</th>
                            <th>Price</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((order) => (
                            <tr key={order.id}>
                                <td>{order.id}</td>
                                <td>{order.buyer_email}</td>
                                <td>{order.seller_email}</td>
                                <td>{order.cert_number}</td>
                                <td>{order.price}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {selectedSection === 'Sets' && sets.length > 0 && (
                <table className="sets-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Series</th>
                            <th>Printed Total</th>
                            <th>Total</th>
                            <th>PTCGO Code</th>
                            <th>Release Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sets.map((set) => (
                            <tr key={set.id}>
                                <td>{set.id}</td>
                                <td>{set.name}</td>
                                <td>{set.series}</td>
                                <td>{set.printed_total}</td>
                                <td>{set.total}</td>
                                <td>{set.ptcgo_code}</td>
                                <td>{new Date(set.release_date).toLocaleDateString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {!loading && selectedSection !== 'Promo' && listings.length === 0 && certs.length === 0 && cards.length === 0 && orders.length === 0 && sets.length === 0 && <p>No data available for this section.</p>}
        </div>
    );
};

export default AdminContent;
